import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ResizablePanel from "../../components/common/ResizablePanel";
import { useLayout } from "../../contexts/LayoutContext";
import { useSettingsDomain } from "../../graphQL/settings/scope";
import { apiGetDeals } from "../../graphQL/deal";
import DealListPanel from "./panels/DealListPanel";
import DealDetailPanel from "./panels/DealDetailPanel";

const PageDeals = () => {
  const { isPanelOpen, togglePanel } = useLayout();
  const domain = useSettingsDomain();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);
  const [deals, setDeals] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  const loadDeals = useCallback(async () => {
    setLoading(true);
    try {
      const { deals: rows } = await apiGetDeals({});
      setDeals(rows);
    } catch (err) {
      setDeals([]);
    } finally {
      setLoading(false);
    }
  }, [domain]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadDeals();
  }, [loadDeals]);

  const filteredDeals = useMemo(() => {
    const needle = searchValue.trim().toLowerCase();
    if (!needle) return deals;
    return deals.filter((deal) =>
      [deal.name, deal.stage, deal.dealType, deal.side]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle)),
    );
  }, [deals, searchValue]);

  return (
    <Box sx={{ display: "flex", height: "100%", minHeight: 0 }}>
      {isPanelOpen("dealList") && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 340}
          minWidth={isMobile ? window.innerWidth : 260}
          maxWidth={isMobile ? window.innerWidth : 560}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <DealListPanel
            onClose={() => togglePanel("dealList")}
            deals={filteredDeals}
            selectedId={selectedDeal?.id}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            onSelect={setSelectedDeal}
            loading={loading}
          />
        </ResizablePanel>
      )}

      {isPanelOpen("dealDetail") && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 560}
          minWidth={isMobile ? window.innerWidth : 320}
          maxWidth={isMobile ? window.innerWidth : 900}
          fillRemaining
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <DealDetailPanel
            onClose={() => togglePanel("dealDetail")}
            deal={selectedDeal}
          />
        </ResizablePanel>
      )}
    </Box>
  );
};

export default PageDeals;
